"use client"

import { useState, useRef, useEffect } from "react"
import { Handle, Position } from "reactflow"
import { Share2, Minus, Plus, MoreHorizontal, ChevronDown, RefreshCw, Table2 } from "lucide-react"
import "./Node.css"
import "./WorkflowNode.css"

const WorkflowNode = ({ data, id }) => {
  const { expanded: initialExpanded = true } = data
  const [isMinimized, setIsMinimized] = useState(true)
  const [workflowType, setWorkflowType] = useState(data.workflowType || "Retool Workflow")
  const [selectedWorkflow, setSelectedWorkflow] = useState(data.selectedWorkflow || "")
  const [runUntil, setRunUntil] = useState(data.runUntil || "Finished")
  const [parameterType, setParameterType] = useState(data.parameterType || "Raw")
  const [parameters, setParameters] = useState(data.parameters || 'ex: { or { key: "value" }')
  const [activeTab, setActiveTab] = useState("Inputs")
  const [showTooltip, setShowTooltip] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [showSquareTooltip, setShowSquareTooltip] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const isDragging = useRef(false)
  const dragStartTime = useRef(0)

  // Add effect to handle panning prevention
  useEffect(() => {
    // Function to prevent mouse events that would cause panning
    const preventPanning = (e) => {
      if (document.body.classList.contains("disable-flow-panning")) {
        // Only prevent default on middle mouse button (wheel) or when dragging with left button
        if (e.button === 1 || (e.button === 0 && e.type === "mousemove" && e.buttons === 1)) {
          e.stopPropagation()
        }
      }
    }

    // Add event listeners to the document
    document.addEventListener("mousedown", preventPanning, { capture: true, passive: false })
    document.addEventListener("mousemove", preventPanning, { capture: true, passive: false })

    // Clean up event listeners on component unmount
    return () => {
      document.removeEventListener("mousedown", preventPanning, { capture: true, passive: false })
      document.removeEventListener("mousemove", preventPanning, { capture: true, passive: false })
    }
  }, [])

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized)
  }

  const handleOpenInTab = () => {
    // Placeholder for opening in tab functionality
    console.log("Open in tab")
  }

  const handleConnectionClick = (event) => {
    event.preventDefault()
    event.stopPropagation()

    // If we're in the middle of a drag operation, don't handle the click
    if (isDragging.current) {
      isDragging.current = false
      return
    }

    // Get the position of the click in screen coordinates
    const position = {
      x: event.clientX,
      y: event.clientY,
    }

    // Open the connection menu at the clicked position
    if (data.onOpenConnectionMenu) {
      data.onOpenConnectionMenu(position, id)
    }
  }

  const handleMouseDown = (event) => {
    // Mark the start of a potential drag operation
    isDragging.current = false
    dragStartTime.current = Date.now()
  }

  const handleMouseUp = (event) => {
    // If the mouse was down for more than a short time, consider it a drag
    if (Date.now() - dragStartTime.current > 200) {
      isDragging.current = true
    }
  }

  return (
    <div
      className={`trigger-node-container workflow-node-container ${isMinimized ? "minimized" : ""} ${isHovering ? "hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Node Header - This part should be draggable */}
      <div className="trigger-node-header">
        <div className="trigger-node-title">
          <Share2 size={16} className="trigger-icon" />
          <span>{data.label || "workflow1"}</span>
        </div>
        <div className="trigger-node-controls">
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag"
              onClick={toggleMinimize}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {isMinimized ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <div className="square-button-container nodrag">
            <button
              className="trigger-node-button nodrag"
              onMouseEnter={() => setShowSquareTooltip(true)}
              onMouseLeave={() => setShowSquareTooltip(false)}
              onClick={handleOpenInTab}
            >
              <Table2 size={16} />
            </button>
            {showSquareTooltip && <div className="square-tooltip">Open in tab</div>}
          </div>
          <button className="trigger-node-button nodrag">
            <MoreHorizontal size={16} />
          </button>
        </div>
      </div>

      {/* Node Content - Should not be draggable and only visible when not minimized */}
      {!isMinimized && (
        <div className="trigger-node-content nodrag">
          {/* Workflow Type Section */}
          <div className="workflow-field nodrag">
            <div className="workflow-select nodrag">
              <span>{workflowType}</span>
              <ChevronDown size={14} />
            </div>
          </div>

          {/* Select Workflow Section */}
          <div className="workflow-field nodrag">
            <label>Select workflow</label>
            <div className="workflow-select nodrag">
              <span className="placeholder">Choose an option</span>
              <ChevronDown size={14} />
            </div>
          </div>

          {/* Run Until Section */}
          <div className="workflow-field nodrag">
            <label>Run until</label>
            <div className="run-until-options nodrag">
              <button
                className={`run-option ${runUntil === "Finished" ? "active" : ""}`}
                onClick={() => setRunUntil("Finished")}
              >
                Finished
              </button>
              <button
                className={`run-option ${runUntil === "Queued" ? "active" : ""}`}
                onClick={() => setRunUntil("Queued")}
              >
                Queued
              </button>
            </div>
          </div>

          {/* Parameters Section */}
          <div className="workflow-field nodrag">
            <div className="parameters-header nodrag">
              <label>Parameters</label>
              <div className="parameter-type-select nodrag">
                <span>{parameterType}</span>
                <ChevronDown size={14} />
              </div>
            </div>
            <textarea
              value={parameters}
              onChange={(e) => setParameters(e.target.value)}
              className="parameters-input nodrag"
              rows={3}
            />
          </div>

          {/* Tabs Section */}
          <div className="workflow-tabs nodrag">
            <button
              className={`workflow-tab ${activeTab === "Inputs" ? "active" : ""}`}
              onClick={() => setActiveTab("Inputs")}
            >
              Inputs
            </button>
            <button
              className={`workflow-tab ${activeTab === "Data" ? "active" : ""}`}
              onClick={() => setActiveTab("Data")}
            >
              Data
            </button>
            <button
              className={`workflow-tab ${activeTab === "JSON" ? "active" : ""}`}
              onClick={() => setActiveTab("JSON")}
            >
              JSON
            </button>
            <button
              className={`workflow-tab ${activeTab === "Settings" ? "active" : ""}`}
              onClick={() => setActiveTab("Settings")}
            >
              Settings
            </button>
            <button className="refresh-button nodrag">
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Output Connection Handle - Should not be draggable */}
      <div
        className="output-handle-container nodrag"
        onMouseEnter={() => {
          setShowTooltip(true)
          setIsHovering(true)
          document.body.classList.add("disable-flow-panning")
        }}
        onMouseLeave={() => {
          setShowTooltip(false)
          document.body.classList.remove("disable-flow-panning")
        }}
        onClick={handleConnectionClick}
        style={{ cursor: "pointer" }}
      >
        {showTooltip && <div className="connection-tooltip nodrag">Click/drag to connect next block</div>}
        <Handle
          type="source"
          position={Position.Right}
          id="a"
          className={`connection-handle output-handle nodrag ${isHovering ? "hovering" : ""}`}
          onClick={handleConnectionClick}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        />
      </div>

      {/* Input Handle - Should not be draggable */}
      <div className="input-handle-container nodrag">
        <Handle
          type="target"
          position={Position.Left}
          id="b"
          className={`connection-handle input-handle nodrag ${isHovering ? "hovering" : ""}`}
        />
      </div>
    </div>
  )
}

export default WorkflowNode
